import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, concatMap, withLatestFrom, tap } from 'rxjs/operators';
import { IStore } from 'src/app/core/root.store';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { IAuthCommon, IAuthTable } from '../auth.interfaces';
import {
    ApproverLogin,
    ApproverLoginSuccess,
    ApproverLoginFailure,
    ApproverAuthActionTypes,
    ApproverLoginSuccessRedirect,
    ApproverRefreshToken,
    ApproverRefreshTokenFailure,
    ApproverRefreshTokenSuccess,
    ApproverDisplayError,
    ApproverCheckLoginFailure,
    ApproverForgotPassword,
    ApproverForgotPasswordComplete,
    ApproverForgotPasswordError,
    ApproverLogout,
    ApproverResetPassword,
    ApproverResetPasswordComplete,
    ApproverCheckLogin,
} from './approver-auth.actions';
import swal from 'sweetalert2';
import { selectDefaultApproverAuthEntity } from './approver-auth.selectors';
import * as moment from 'moment';
import { UserLogin } from 'src/app/shared/services/roles.service';
import { ApproverAuthService } from '../../services/approver-auth.service';
import { ApproverSetIsApprover, FetchLotsAndUnits, SetIsLoggedInFalse, SetIsLoggedInTrue, SetLoginCheckComplete } from 'src/app/core/store/ui/ui.actions';
import { getStrataPlanNumberFriendlyName } from 'src/app/core/store/ui/ui.selectors';
import { RemoveAllProfiles } from 'src/app/core/store/resident-profile/resident-profile.actions';
import { LoginSuccess } from '../auth.actions';
import { SearcherLoginSuccess } from '../searcher/searcher-auth.actions';


@Injectable()
export class ApproverAuthEffects {
    
    login$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverLogin>(ApproverAuthActionTypes.APPROVER_LOGIN),
        switchMap((action) => {
            return this.approverAuthService.approverLogin(action.payload).pipe(
                map((authResponse: IAuthCommon) => {
                    localStorage.setItem(
                        environment.auth.token,
                        authResponse.accessToken
                    );
                    localStorage.setItem(
                        environment.auth.token_expiry,
                        authResponse.expiresIn.toString()
                    );
                    localStorage.setItem(
                        environment.auth.refresh_token,
                        authResponse.refreshToken
                    );

                    let authState: IAuthTable = {
                        entities: {
                            '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                                accessToken: authResponse.accessToken,
                                expiresIn: authResponse.expiresIn,
                                refreshToken: authResponse.refreshToken,
                                loggedIn: true,
                            },
                        },
                        ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                    };
                    localStorage.setItem(environment.ui.logged_user_name, action.payload.userName);
                    localStorage.setItem(environment.auth.user_login, UserLogin.Approver);
                    return new ApproverLoginSuccess(authState);
                }),
                catchError((error) => {
                    return of(new ApproverLoginFailure(error));
                })
            );
        })
    ));

    
    loginSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverLoginSuccess>(ApproverAuthActionTypes.APPROVER_LOGIN_SUCCESS),
        withLatestFrom(
            this.store$.select(
                selectDefaultApproverAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
            )
        ),
        map(([action, authUser]) => {
            return authUser;
        }), withLatestFrom(
            this.store$.select(getStrataPlanNumberFriendlyName)
        ), map(([action, authUser]) => {
            return authUser;
        }),
        concatMap((strataPlanNumber) => [
            new SetLoginCheckComplete(),
            new SetIsLoggedInTrue(),
            new ApproverSetIsApprover(),
            new ApproverLoginSuccessRedirect(),
            new FetchLotsAndUnits(strataPlanNumber),
            new RemoveAllProfiles()
        ])
    ));

    
    loginRedirect$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverLoginSuccessRedirect>(ApproverAuthActionTypes.APPROVER_LOGIN_SUCCESS_REDIRECT),
        tap(() => {
            if (localStorage.getItem(environment.auth.user_login) === UserLogin.Approver) {
                this.router.navigate([environment.auth.auth_success_url_approver]);
            }
        })
    ), { dispatch: false });

    
    loginError$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverLoginFailure>(ApproverAuthActionTypes.APPROVER_LOGIN_FAILURE),
        tap((err: any) => { })
    ), { dispatch: false });

    
    refreshTokenError$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverRefreshTokenFailure>(ApproverAuthActionTypes.APPROVER_REFRESH_TOKEN_FAILURE),
        concatMap((err: any) => [
            new ApproverDisplayError('Your session has timed out. Please login again.'),
            new ApproverLogout(),
        ])
    ));


    
    refreshToken$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverRefreshToken>(ApproverAuthActionTypes.APPROVER_REFRESH_TOKEN),
        withLatestFrom(
            this.store$.select(
                selectDefaultApproverAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
            )
        ),
        switchMap(([, authUser]) => {
            if (!authUser) {
                return of(new ApproverLogout());
            }

            return this.authService.refreshToken(authUser.refreshToken).pipe(
                map((authResponse: IAuthCommon) => {
                    localStorage.setItem(
                        environment.auth.token,
                        authResponse.accessToken
                    );
                    localStorage.setItem(
                        environment.auth.token_expiry,
                        authResponse.expiresIn.toString()
                    );
                    localStorage.setItem(
                        environment.auth.refresh_token,
                        authResponse.refreshToken
                    );

                    let authState: IAuthTable = {
                        entities: {
                            '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                                accessToken: authResponse.accessToken,
                                expiresIn: authResponse.expiresIn,
                                refreshToken: authResponse.refreshToken,
                                loggedIn: true,
                            },
                        },
                        ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                    };
                    return new ApproverRefreshTokenSuccess(authState);
                }),
                catchError((error) => {
                    return of(new ApproverRefreshTokenFailure(error));
                })
            );
        }),
        concatMap(() => [
            new ApproverSetIsApprover(),
        ])
    ));

    
    approverLogout$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverLogout>(ApproverAuthActionTypes.APPROVER_LOGOUT),
        withLatestFrom(this.store$.select(getStrataPlanNumberFriendlyName)),
        map(([action, friendlyName]) => {
            localStorage.removeItem(environment.auth.token);
            localStorage.removeItem(environment.auth.token_expiry);
            localStorage.removeItem(environment.auth.refresh_token);
            localStorage.removeItem(environment.auth.user_login);
            localStorage.removeItem(environment.ui.logged_user_name);
            return new SetIsLoggedInFalse();
        })
    ));


    
    checkLogin$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverCheckLogin>(ApproverAuthActionTypes.APPROVER_CHECK_LOGIN),
        map((action) => {
            let accessToken = localStorage.getItem(environment.auth.token);
            let userLogin = localStorage.getItem(environment.auth.user_login);
            if (accessToken) {
                let expiresIn = +localStorage.getItem(environment.auth.token_expiry);
                let refreshToken = localStorage.getItem(environment.auth.refresh_token);
                let authState: IAuthTable = {
                    entities: {
                        '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                            accessToken,
                            expiresIn,
                            refreshToken,
                            loggedIn: true,
                        },
                    },
                    ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
                };
                const currentUtcUnixTime = moment().utc().unix();
                if (expiresIn > currentUtcUnixTime) {
                    if (localStorage.getItem(environment.auth.user_login) === UserLogin.Approver) {
                        return new ApproverLoginSuccess(authState);
                    }
                    if (localStorage.getItem(environment.auth.user_login) === UserLogin.Searcher) {
                        return new SearcherLoginSuccess(authState);
                    }
                    else {
                        return new LoginSuccess(authState);
                    }
                } else {
                    return new ApproverCheckLoginFailure();
                }
            } else {
                return new SetLoginCheckComplete();
            }
        })
    ));

    
    forgotPassword$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverForgotPassword>(ApproverAuthActionTypes.APPROVER_FORGOT_PASSWORD),
        switchMap((action) => {
            return this.approverAuthService.forgotPassword(action.payload).pipe(
                map(() => new ApproverForgotPasswordComplete()),
                catchError((err) => of(new ApproverForgotPasswordError(err)))
            );
        })
    ));

    
    resetPassword$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverResetPassword>(ApproverAuthActionTypes.APPROVER_RESET_PASSWORD),
        switchMap((action) => {
            return this.approverAuthService.resetPassword(action.payload).pipe(
                map(() => new ApproverResetPasswordComplete()),
                catchError((err) => of(new ApproverDisplayError(err))) // TODO: create its own error effect to not show random errors.
            );
        })
    ));

    
    checkLoginFailure$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverCheckLoginFailure>(ApproverAuthActionTypes.APPROVER_CHECK_LOGIN_FAILURE),
        concatMap(() => [
            new SetLoginCheckComplete(),
            new SetIsLoggedInFalse(),
            new ApproverLogout(),
        ])
    ));

    
    displayError$ = createEffect(() => this.actions$.pipe(
        ofType<ApproverDisplayError>(ApproverAuthActionTypes.APPROVER_DISPLAY_ERROR),
        tap((action) => {
            swal.fire({
                position: 'center',
                icon: 'info',
                title: action.payload,
                showConfirmButton: true,
                timer: 5000,
            });
        })
    ), { dispatch: false });


    constructor(
        private actions$: Actions,
        private router: Router,
        private approverAuthService: ApproverAuthService,
        private authService: AuthService,
        private store$: Store<IStore>
    ) { }
}

